import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormProvider, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import classnames from 'classnames';
import {
  useIsBreakpointDown,
  useDialogCancelAction
} from 'modules/common/hooks';
import { getLabelForType } from 'modules/timesheet-labels/hooks';
import { LabelType } from 'graphql/types';
import { LabelsFormFieldContextProvider } from 'modules/timesheet-labels';
import { FormTimesheetLabel } from 'modules/timesheet-labels/labelSchema';

import { useLabels } from 'modules/common/hooks';
import { FormErrorDialogContextProvider } from 'modules/notes/common';
import { useScrollbarStyles } from 'modules/common/styles';
import { CustomLabels } from 'modules/customLabels/components/CustomLabels';
import { useTrackRenderCounts } from 'modules/common/components/Render';
import { NoteContextProvider } from '../NoteContext';
import {
  NOTE_FIELDS,
  defaultDateTime,
  NoteBody,
  NoteFooter,
  NoteHeader,
  TimerContextProvider
} from '../common';
import TimesheetLabelsContent from './TimesheetLabelsContent';
import {
  useCreateTimeSignal,
  useNoteForm,
  useFormChangeHandlers
} from './hooks';

const useStyles = makeStyles((theme) => ({
  title: {
    padding: theme.spacing(2, 1.5, 1),
    color: theme.palette.grey[700]
  },
  content: {
    padding: theme.spacing(0)
  },
  footer: {
    padding: theme.spacing(1, 2.5)
  },
  noteBody: {
    paddingLeft: 0
  }
}));

export const useDialogStyles = makeStyles((theme) => ({
  paperScrollPaper: {
    minWidth: 'calc(100% - 64px)',
    [theme.breakpoints.only('sm')]: {
      maxWidth: '100%'
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 'calc(100% - 64px)'
    }
  }
}));

type AddNoteDialogProps = {
  open: boolean;
  onClose: () => void;
  startTime?: Date;
  endTime?: Date;
  date?: DateTime;
};

export const AddNoteDialog: React.FC<AddNoteDialogProps> = ({
  open,
  onClose,
  startTime,
  endTime,
  date: defaultDate
}: AddNoteDialogProps) => {
  useTrackRenderCounts('add-note-dialog');
  const classes = useStyles();
  const classesScrollbar = useScrollbarStyles();
  const [date, setDate] = useState(defaultDate ?? defaultDateTime());

  const {
    timesheetLabels,
    showStartEndTime,
    loading: labelLoading
  } = useLabels(date);

  const defaultValues = useMemo(
    () => ({
      startTime,
      endTime,
      date: defaultDate?.toJSDate()
    }),
    [defaultDate, endTime, startTime]
  );
  const {
    formMethods,
    formMethods: { handleSubmit, control },
    isFormDirty,
    isValid,
    errorMessage,
    timesheetLabelFields
  } = useNoteForm({ timesheetLabels, defaultTimeValues: defaultValues });
  const formChangeHandlers = useFormChangeHandlers(formMethods);
  const { onAddNote, loading } = useCreateTimeSignal({ onClose });

  const { index: commentLabelIndex } = getLabelForType(
    LabelType.Comment,
    timesheetLabelFields
  );
  const cancelActionProps = useDialogCancelAction({
    onClose,
    isFormDirty,
    isValidForm: isValid && isFormDirty,
    loading
  });
  const { onNoteDiscard } = cancelActionProps;
  const { formatMessage } = useIntl();

  const fullScreen = useIsBreakpointDown('xs');
  const calendarDate = useWatch({ control, name: NOTE_FIELDS.DATE });

  useEffect(() => {
    if (
      calendarDate &&
      DateTime.fromJSDate(calendarDate).toISODate() !== date.toISODate()
    ) {
      setDate(DateTime.fromJSDate(calendarDate!));
    }
  }, [date, setDate, calendarDate]);

  const noteBodyClasses = useMemo(() => ({ mainContainer: classes.noteBody }), [
    classes.noteBody
  ]);

  return (
    <FormProvider {...formMethods}>
      <FormErrorDialogContextProvider>
        <NoteContextProvider date={date}>
          <TimerContextProvider>
            <Dialog
              data-testid="add-note-dialog"
              open={open}
              onClose={onNoteDiscard}
              classes={useDialogStyles()}
              fullScreen={fullScreen}
            >
              <DialogTitle
                data-testid="add-note-dialog-title"
                className={classes.title}
              >
                <NoteHeader
                  title={formatMessage({ id: 'note.addNoteTitle' })}
                />
              </DialogTitle>
              <DialogContent
                data-testid="add-note-dialog-content"
                className={classnames(
                  classes.content,
                  classesScrollbar.scrollbar
                )}
              >
                <NoteBody
                  errorMessage={errorMessage}
                  formChangeHandlers={formChangeHandlers}
                  showStartEndTime={showStartEndTime}
                  commentLabelIndex={commentLabelIndex}
                  classes={noteBodyClasses}
                />
                <CustomLabels fieldName={NOTE_FIELDS.CUSTOM_LABELS} />
                <LabelsFormFieldContextProvider
                  timesheetLabels={NOTE_FIELDS.TIMESHEET_LABELS}
                  note={NOTE_FIELDS.NOTE}
                >
                  <TimesheetLabelsContent
                    loading={labelLoading}
                    fields={timesheetLabelFields as FormTimesheetLabel[]}
                  />
                </LabelsFormFieldContextProvider>
              </DialogContent>
              <DialogActions
                data-testid="add-note-dialog-footer"
                className={classes.footer}
              >
                <NoteFooter
                  saveTitle={formatMessage({ id: 'note.addNote' })}
                  handleSubmit={handleSubmit}
                  onSubmitNote={onAddNote}
                  onSaveNoteWithTimerRunning={formChangeHandlers.handleDuration}
                  showStartEndTime={showStartEndTime}
                  loading={loading}
                  onClassificationChange={
                    formChangeHandlers.onChangeClassification
                  }
                  mode="add"
                  {...cancelActionProps}
                  customLabels={NOTE_FIELDS.CUSTOM_LABELS}
                  timesheetLabels={NOTE_FIELDS.TIMESHEET_LABELS}
                />
              </DialogActions>
            </Dialog>
          </TimerContextProvider>
        </NoteContextProvider>
      </FormErrorDialogContextProvider>
    </FormProvider>
  );
};
